import { CheckCircle, Award, Clock, BookText } from 'lucide-react';
import { CourseAssignment } from '../../types';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';
import { useEmployeeStore } from '../../stores/employeeStore';
import { useEffect } from 'react';

function ShimmerCard() {
    return (
        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200">
            <div className="animate-pulse space-y-4">
                <div className="h-6 w-1/3 bg-slate-200 rounded"></div>
                <div className="space-y-3">
                    <div className="h-4 w-full bg-slate-200 rounded"></div>
                    <div className="h-4 w-2/3 bg-slate-200 rounded"></div>
                    <div className="h-4 w-1/2 bg-slate-200 rounded"></div>
                </div>
            </div>
        </div>
    );
}

export function DashboardPage() {
    const user = useAuthStore(state => state.user);
    const navigate = useNavigate();
    const { userCourses, isLoading, error, fetchEmployeeData } = useEmployeeStore();

    useEffect(() => {
        fetchEmployeeData();
    }, [fetchEmployeeData]);

    if (isLoading) {
        return (
            <div>
                <div className="h-8 w-1/4 bg-slate-200 rounded animate-pulse mb-6"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    <ShimmerCard />
                    <ShimmerCard />
                    <ShimmerCard />
                    <ShimmerCard />
                    <ShimmerCard />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">{error}</p>
            </div>
        );
    }

    const coursesArray = Object.values(userCourses);
    
    // Get courses that are due soon (status === 'due' OR due within next 45 days)
    const now = new Date();
    const fortyFiveDaysFromNow = new Date(now.getTime() + (45 * 24 * 60 * 60 * 1000));
    
    const dueSoon = coursesArray
        .filter(course => {
            // Include courses with status 'due'
            if (course.status === 'due') return true;
            
            // Include courses due within next 45 days
            if (course.due_date) {
                const dueDate = new Date(course.due_date);
                return dueDate >= now && dueDate <= fortyFiveDaysFromNow;
            }
            
            return false;
        })
        .sort((a, b) => {
            // Sort by due date (soonest first)
            const dateA = new Date(a.due_date || '');
            const dateB = new Date(b.due_date || '');
            return dateA.getTime() - dateB.getTime();
        })
        .slice(0, 3);
    
    const inProgress = coursesArray.filter(c => c.status === 'inProgress').slice(0, 3);
    const recentlyCompleted = coursesArray
        .filter(c => c.status === 'completed')
        .sort((a, b) => new Date(b.completed_date || '').getTime() - new Date(a.completed_date || '').getTime())
        .slice(0, 3);

    const handleViewCourse = (courseId: string) => {
        const course = userCourses[courseId];
        navigate(`/courses/${courseId}`, { state: { course } });
    };

    const getCourseProgress = (course: CourseAssignment): number => {
        // Since we don't have lessons, we'll use the status to determine progress
        switch (course.status) {
            case 'completed':
                return 100;
            case 'inProgress':
                return 50; // Assume 50% for in-progress courses
            case 'due':
                return 25; // Assume 25% for due courses
            case 'notStarted':
            default:
                return 0;
        }
    };

    return (
        <div>
            <h2 className="text-2xl font-semibold text-foreground mb-6">Welcome back, {user?.name || 'there'}!</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {/* Due Soon Card */}
                <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Due Soon</h3>
                    {dueSoon.length > 0 ? (
                        <div className="space-y-4">
                            {dueSoon.map(course => (
                                <div key={course.id} className="border-l-4 border-orange-400 pl-3">
                                    <p className="font-medium text-foreground">{course.courses.title}</p>
                                    <p className="text-sm text-slate-500">Due: {new Date(course.due_date + 'T00:00:00').toLocaleDateString()}</p>
                                    <button onClick={() => handleViewCourse(course.course_id)} className="mt-1 text-sm text-primary-600 hover:underline font-medium">
                                        {getCourseProgress(course) > 0 ? 'Resume Course' : 'Start Course'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                            <Clock className="w-12 h-12 text-slate-400 mb-4" />
                            <p className="text-slate-600 mb-2">No courses due soon</p>
                            <p className="text-sm text-slate-500">You're all caught up with your training!</p>
                        </div>
                    )}
                </div>
                {/* In Progress Card */}
                <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200">
                    <h3 className="text-lg font-semibold text-foreground mb-4">In Progress</h3>
                    {inProgress.length > 0 ? (
                        <div className="space-y-4">
                            {inProgress.map(course => (
                                <div key={course.id}>
                                    <p className="font-medium text-foreground">{course.courses.title}</p>
                                    <div className="w-full bg-slate-200 rounded-full h-2.5 mt-1 mb-2">
                                        <div className="bg-primary-600 h-2.5 rounded-full" style={{ width: `${getCourseProgress(course)}%` }}></div>
                                    </div>
                                    <p className="text-sm text-slate-500 mb-1">{getCourseProgress(course)}% Complete</p>
                                    <button onClick={() => handleViewCourse(course.course_id)} className="text-sm text-primary-600 hover:underline font-medium">Continue Course</button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                            <BookText className="w-12 h-12 text-slate-400 mb-4" />
                            <p className="text-slate-600 mb-2">No courses in progress</p>
                            <p className="text-sm text-slate-500">Start a new course to begin your learning journey</p>
                        </div>
                    )}
                </div>
                {/* Recently Completed Card */}
                <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Recently Completed</h3>
                    {recentlyCompleted.length > 0 ? (
                        <div className="space-y-3">
                            {recentlyCompleted.map(course => {
                                return (
                                    <div key={course.id} className="flex items-center text-sm">
                                        <CheckCircle size={16} className="text-green-500 mr-2 flex-shrink-0"/>
                                        <div>
                                            <p className="font-medium text-foreground">{course.courses.title}</p> 
                                            <p className="text-xs text-slate-500">
                                                Completed: {course.completed_date ? new Date(course.completed_date).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                            <Award className="w-12 h-12 text-slate-400 mb-4" />
                            <p className="text-slate-600 mb-2">No completed courses yet</p>
                            <p className="text-sm text-slate-500">Complete your first course to earn a certificate</p>
                        </div>
                    )}
                </div>
                {/* Announcements Card
                <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200 xl:col-span-3">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Announcements</h3>
                    <div className="space-y-3 text-sm text-slate-600">
                        {announcements.length > 0 ? (
                            announcements.map(announcement => (
                                <div key={announcement.id} className="border-l-2 pl-3 border-primary-200">
                                    <p className="font-medium text-primary-700">{announcement.title}</p>
                                    <p className="text-slate-600">{announcement.content}</p>
                                    {announcement.type === 'course' && announcement.id && (
                                        <button 
                                            onClick={() => handleViewCourse(announcement.id)}
                                            className="mt-1 text-xs text-primary-600 hover:underline"
                                        >
                                            View Course
                                        </button>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                                <Bell className="w-12 h-12 text-slate-400 mb-4" />
                                <p className="text-slate-600 mb-2">No current announcements</p>
                                <p className="text-sm text-slate-500">Check back later for updates and news</p>
                            </div>
                        )}
                    </div>
                </div> */}
            </div>
        </div>
    );
}
